import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';

interface AddNewRegistryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (itemName: string) => void;
  project: DashboardProject;
  existingItems: string[];
}

export const AddNewRegistryItemModal: React.FC<AddNewRegistryItemModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  project,
  existingItems
}) => {
  const [itemName, setItemName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Convert string to title case (capitalize first letter of each word)
  const toTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleAdd = async () => {
    if (!itemName.trim()) {
      customNotification.warning('Please enter registry item name');
      return;
    }

    // Convert to title case
    const titleCaseName = toTitleCase(itemName.trim());

    // Check if item already exists
    if (existingItems.some(item => item.toLowerCase() === titleCaseName.toLowerCase())) {
      customNotification.warning(`${titleCaseName} already exists`);
      return;
    }

    setIsAdding(true);
    try {
      const apiPayload = {
        tags_name: titleCaseName,
        project_id: Number(project.project.projectId),
        tags_type: 'REGISTRY_ITEM', // Using uppercase with underscore pattern
        created_by: user.email || ''
      };

      const res = await AddMaterialCategory(apiPayload, 'Registry item');

      if (res) {
        customNotification.success(
          'Registry item added successfully',
          `The registry item "${titleCaseName}" has been added successfully.`
        );
        onAdd(titleCaseName);
        handleClose();
      }
    } catch (error) {
      customNotification.error(
        'Failed to add registry item',
        'There was an error adding the registry item. Please try again.'
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setItemName('');
    setIsAdding(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add New Registry Item
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="itemName">Registry Item Name</Label>
            <Input
              id="itemName"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="Enter registry item name (e.g., Welding, NDE, QC)"
              className="w-full"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isAdding && itemName.trim()) {
                  handleAdd();
                }
              }}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex items-center gap-2"
            disabled={isAdding}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={isAdding || !itemName.trim()}
            loading={isAdding}
            loadingText="Adding..."
            className="flex items-center gap-2 bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90"
          >
            <Plus className="h-4 w-4" />
            Add Registry Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

